import { useState } from "react";
import { Badge, Button, StatusBadge } from "@codestack/ui";
import { apiRequest, ApiError } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { EmptyState, ErrorState, InlineAlert, LoadingState } from "../components/States";
import { dateTime, money } from "../lib/format";

type RunSummary = {
  runId: string;
  matched: number;
  mismatch: number;
  missingLocal: number;
  missingProvider: number;
  createdAt: string;
};

type RecordRow = {
  id: string;
  paymentId: string | null;
  providerRef: string | null;
  expectedMinor: string | null;
  actualMinor: string | null;
  currency: string;
  status: string;
  notes: string | null;
  createdAt: string;
};

export function ReconciliationPage() {
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<RunSummary[]>("/admin/reconciliation");
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);

  const records = useApi<RecordRow[]>(
    selectedRun ? `/admin/reconciliation/${selectedRun}` : null,
  );

  const triggerRun = async () => {
    setRunning(true);
    setRunMessage(null);
    try {
      const result = await apiRequest<{ jobId: string; status: string }>(
        "/admin/reconciliation/run",
        { method: "POST" },
      );
      setRunMessage(
        `Reconciliation queued (job ${result.jobId.slice(0, 8)}). Results appear once the worker completes the run.`,
      );
      reload();
    } catch (cause) {
      setRunMessage(
        cause instanceof ApiError ? cause.message : "The reconciliation run could not be queued.",
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-white">Reconciliation</h1>
          <p className="text-sm text-ink-400">
            Compare local payment records against the Squad ledger.
          </p>
        </div>
        {can("reconciliation:run") ? (
          <Button onClick={triggerRun} disabled={running}>
            {running ? "Queueing…" : "Run reconciliation"}
          </Button>
        ) : null}
      </div>

      {runMessage ? <InlineAlert tone="info">{runMessage}</InlineAlert> : null}

      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {loading && !data ? <LoadingState label="Loading reconciliation runs…" /> : null}

      {data && data.length === 0 ? (
        <EmptyState
          title="No reconciliation runs yet"
          description="Runs execute hourly in the worker. Trigger one manually to compare the latest payments."
          action={
            can("reconciliation:run") ? (
              <Button size="sm" onClick={triggerRun} disabled={running}>
                Run now
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {data && data.length > 0 ? (
        <div className="grid gap-5 xl:grid-cols-[1fr_1.1fr]">
          <div className="table-shell">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="table-head">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Run</th>
                    <th className="px-4 py-3 text-right font-semibold">Matched</th>
                    <th className="px-4 py-3 text-right font-semibold">Issues</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {data.map((run) => {
                    const issues = run.mismatch + run.missingLocal + run.missingProvider;
                    const isSelected = selectedRun === run.runId;
                    return (
                      <tr
                        key={run.runId}
                        onClick={() => setSelectedRun(run.runId)}
                        className={`cursor-pointer transition-colors ${
                          isSelected ? "bg-brand-500/[0.08]" : "hover:bg-white/[0.02]"
                        }`}
                      >
                        <td className="px-4 py-3">
                          <p className="font-mono text-[11px] text-ink-200">
                            {run.runId.slice(0, 12)}
                          </p>
                          <p className="mt-0.5 text-[10px] text-ink-600">
                            {dateTime(run.createdAt)}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-emerald-200/90">
                          {run.matched}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-ink-300">
                          {issues}
                        </td>
                        <td className="px-4 py-3">
                          {issues === 0 ? (
                            <Badge tone="success">Clean</Badge>
                          ) : (
                            <Badge tone="danger">{issues} to review</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <section className="admin-card p-5">
            <h2 className="font-display text-sm font-semibold text-white">Run detail</h2>
            {!selectedRun ? (
              <p className="mt-3 text-xs text-ink-500">
                Select a run to inspect individual records.
              </p>
            ) : records.loading && !records.data ? (
              <div className="mt-4">
                <LoadingState label="Loading records…" />
              </div>
            ) : records.error ? (
              <div className="mt-4">
                <ErrorState message={records.error} onRetry={records.reload} />
              </div>
            ) : (
              <ul className="mt-4 space-y-2">
                {(records.data ?? []).map((record) => (
                  <li
                    key={record.id}
                    className="rounded-lg border border-white/[0.07] bg-white/[0.02] p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[11px] text-ink-300">
                        {record.providerRef ?? record.paymentId ?? "—"}
                      </span>
                      <StatusBadge status={record.status} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-ink-500">
                      <span>
                        Expected{" "}
                        <span className="font-mono text-ink-300">
                          {record.expectedMinor
                            ? money(record.expectedMinor, record.currency)
                            : "—"}
                        </span>
                      </span>
                      <span>
                        Actual{" "}
                        <span className="font-mono text-ink-300">
                          {record.actualMinor ? money(record.actualMinor, record.currency) : "—"}
                        </span>
                      </span>
                    </div>
                    {record.notes ? (
                      <p className="mt-1.5 text-[11px] text-ink-500">{record.notes}</p>
                    ) : null}
                  </li>
                ))}
                {(records.data ?? []).length === 0 ? (
                  <li className="text-xs text-ink-500">No records in this run.</li>
                ) : null}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
